import MenuOptions from '@/@straico-ui/commons/MenuOptions';
import MiniAudioPlayer from '@/Features/Core/Components/ActionsCapabilities/components/miniAudioPlayer';
import { useCustomAction } from '@/Features/Core/Components/ActionsCapabilities/hooks/useCustomAction';
import {
  ExclamationCircleIcon,
  QuestionMarkCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { MenuItem, Slider, TextField, Tooltip } from '@mui/material';
import { memo } from 'react';
import { defaultMenuOptionsProps, renderIcon } from '../utils';
import ActionButton from './ActionButton';

const ModelParameter = ({
  parameter,
  selectedModelParameters,
  setSelectedModelParameters,
}) => {
  const isRequired = parameter.required;
  const customAction = useCustomAction(parameter.property);
  const hasValue =
    (selectedModelParameters ?? {})[parameter.property] !== undefined &&
    (selectedModelParameters ?? {})[parameter.property] !== '';
  const showError = isRequired && !hasValue;

  const getDisplayValue = () => {
    if (selectedModelParameters?.[parameter.property] === undefined) return '';
    return parameter.type === 'dropdown'
      ? parameter.options.find(
          // eslint-disable-next-line eqeqeq
          (opt) => opt.value == selectedModelParameters[parameter.property]
        )?.label ?? selectedModelParameters[parameter.property]
      : selectedModelParameters[parameter.property];
  };

  const handleParameterChange = (value) => {
    setSelectedModelParameters({
      ...selectedModelParameters,
      [parameter.property]: value,
    });
  };

  const handleDeleteParameter = () => {
    const newParameters = { ...selectedModelParameters };
    delete newParameters[parameter.property];
    setSelectedModelParameters(newParameters);
  };

  const renderParameterInput = (handleClose) => {
    switch (parameter.type) {
      case 'dropdown':
        return (
          <>
            <div className="flex items-center gap-2 p-2">
              <label className="text-sm font-medium font-figtree">
                {parameter.label}
              </label>
              {parameter.description && (
                <Tooltip title={parameter.description} placement="right">
                  <QuestionMarkCircleIcon className="size-4 text-gray-400" />
                </Tooltip>
              )}
            </div>
            {parameter.options.map((option, index) => (
              <MenuItem
                selected={
                  (selectedModelParameters ?? {})[parameter.property] ===
                  option.value
                }
                key={parameter.name + '-' + index + '-option'}
                onClick={() => {
                  const currentValue =
                    selectedModelParameters?.[parameter.property];
                  if (currentValue === option.value) {
                    handleDeleteParameter();
                  } else {
                    handleParameterChange(option.value);
                  }
                  handleClose();
                }}
              >
                <div className="flex items-center mr-2">
                  {option.preview_url && (
                    <MiniAudioPlayer
                      previewUrl={option.preview_url}
                      audioId={`${parameter.property}-${option.value}`}
                    />
                  )}
                </div>
                <p className="capitalize">{option.label}</p>
              </MenuItem>
            ))}
          </>
        );
      case 'number':
        return (
          <div className="p-2">
            <div className="flex items-center gap-2 mb-2">
              <label className="text-sm font-medium">{parameter.label}</label>
              {parameter.description && (
                <Tooltip title={parameter.description} placement="right">
                  <QuestionMarkCircleIcon className="size-4 text-gray-400" />
                </Tooltip>
              )}
            </div>
            <TextField
              size="small"
              type="number"
              label={parameter.label}
              value={selectedModelParameters?.[parameter.property] ?? ''}
              onChange={(e) => {
                const value =
                  e.target.value === '' ? '' : Number(e.target.value);
                if (value === '') {
                  handleDeleteParameter();
                } else {
                  handleParameterChange(value);
                }
              }}
              inputProps={{
                min: parameter.min,
                max: parameter.max,
                step: parameter.step || 1,
              }}
            />
          </div>
        );
      case 'slider':
        return (
          <div className="p-4 w-64">
            <div className="flex items-center gap-2 mb-2">
              <label className="text-sm font-medium">{parameter.label}</label>
              {parameter.description && (
                <Tooltip title={parameter.description} placement="right">
                  <QuestionMarkCircleIcon className="size-4 text-gray-400" />
                </Tooltip>
              )}
            </div>
            <Slider
              value={
                selectedModelParameters?.[parameter.property] ??
                parameter.min ??
                0
              }
              onChange={(e, value) => {
                e.stopPropagation();
                if (value === parameter.min) {
                  handleDeleteParameter();
                } else {
                  handleParameterChange(value);
                }
              }}
              min={parameter.min ?? 0}
              max={parameter.max ?? 100}
              step={parameter.step ?? 1}
              marks={[
                { value: parameter.min ?? 0, label: parameter.min ?? 0 },
                { value: parameter.max ?? 100, label: parameter.max ?? 100 },
              ]}
              valueLabelDisplay="auto"
            />
          </div>
        );
      case 'text':
        return (
          <div className="p-2">
            <div className="flex items-center gap-2 mb-2">
              <label className="text-sm font-medium">{parameter.label}</label>
              {parameter.description && (
                <Tooltip title={parameter.description} placement="right">
                  <QuestionMarkCircleIcon className="size-4 text-gray-400" />
                </Tooltip>
              )}
            </div>
            <TextField
              size="small"
              type="text"
              multiline
              maxRows={3}
              label={parameter.label}
              value={selectedModelParameters?.[parameter.property] ?? ''}
              onChange={(e) => {
                if (e.target.value === '') {
                  handleDeleteParameter();
                } else {
                  handleParameterChange(e.target.value);
                }
              }}
              fullWidth
              inputProps={{
                maxLength: parameter.maxLength,
              }}
            />
          </div>
        );
      default:
        return null;
    }
  };

  // Para booleanos, renderizamos directamente el ActionButton sin MenuOptions
  if (parameter.type === 'boolean' || parameter.type === 'toggle') {
    return (
      <ActionButton
        tooltipRenderHtml
        tooltip={parameter.description}
        icon={renderIcon(parameter.icon)}
        className={`w-fit`}
        text={parameter.label}
        active={(selectedModelParameters ?? {})[parameter.property] ?? false}
        onClick={() =>
          handleParameterChange(
            !((selectedModelParameters ?? {})[parameter.property] ?? false)
          )
        }
        endContent={
          showError ? (
            <Tooltip title="This field is required">
              <ExclamationCircleIcon className="size-4 text-red-500" />
            </Tooltip>
          ) : null
        }
      />
    );
  }

  if (parameter.type === 'action') {
    return (
      <ActionButton
        tooltipRenderHtml
        tooltip={parameter.description}
        icon={renderIcon(parameter.icon)}
        className={`w-fit`}
        text={parameter.label}
        active={selectedModelParameters?.[parameter.property] ?? false}
        onClick={() => {
          if (parameter.type === 'action') {
            customAction();
          }
        }}
        endContent={
          showError ? (
            <Tooltip title="This field is required">
              <ExclamationCircleIcon className="size-4 text-red-500" />
            </Tooltip>
          ) : null
        }
      />
    );
  }

  // Para otros tipos, usamos MenuOptions
  return (
    <MenuOptions
      {...defaultMenuOptionsProps}
      title={
        <div className="relative group">
          <ActionButton
            tooltipRenderHtml
            className={`w-max`}
            icon={renderIcon(parameter.icon)}
            tooltip={`
              <p>${parameter.label}${
              getDisplayValue() ? `: ${getDisplayValue()}` : ''
            }</p>
              ${parameter.description ? `${parameter.description}` : ''}
            `}
            text={
              selectedModelParameters?.[parameter.property] !== undefined
                ? `${getDisplayValue()}`
                : parameter.label
            }
            active={(selectedModelParameters ?? {}).hasOwnProperty(
              parameter.property
            )}
            endContent={
              showError ? (
                <Tooltip title="This field is required">
                  <ExclamationCircleIcon className="size-4 text-red-500" />
                </Tooltip>
              ) : null
            }
          />
          {selectedModelParameters?.[parameter.property] !== undefined && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteParameter();
              }}
              className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white rounded-full p-0.5 shadow-sm hover:bg-gray-100"
            >
              <XMarkIcon className="size-3 text-gray-500" />
            </button>
          )}
        </div>
      }
      customChildren={renderParameterInput}
    />
  );
};

export default memo(ModelParameter);
